import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols/index.d.ts";
import { type CSSProperties, forwardRef, type ReactElement, type ReactNode } from "react";
import { mergeProps, useHover, VisuallyHidden } from "react-aria";

import { useDataAttributes, useDOMRef } from "../../hooks.ts";
import style from "../ContextualMenu/ContextualMenu.module.css";
import { Icon } from "../Icon/Icon.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import type { CollectionItem } from "../List/MenuListBox.tsx";
import { Shortcut } from "../Shortcut/Shortcut.tsx";
import { TagIntent } from "../Tag/settings.ts";
import { Tag } from "../Tag/Tag.tsx";
import { MenuOptionItemIntent } from "./settings.ts";

export interface MenuOptionItemProps extends CollectionItem {
  /** label */
  title: ReactNode;
  icon?: ReactElement | MaterialSymbol;
  description?: ReactNode;
  tag?: ReactNode | string;
  shortcut?: string;
  intent?: MenuOptionItemIntent;
  id?: string;
  style?: CSSProperties;
  className?: string;
  useHover?: boolean;
  tipId?: string; // getting started tip
  noCheck?: boolean;
}

// TODO: useMenuItem + get state instead of isDisabled, etc... from MenuListBox/MenuOption
// TODO: Add shortcut and trigger onClick/onAction
export const MenuOptionItem = forwardRef<HTMLDivElement, MenuOptionItemProps>(function MenuOptionItem(props, ref) {
  // Extract from CollectionItem
  const { isFocused, isDisabled, isSelected } = props;
  const triggerRef = useDOMRef(ref);
  const { hoverProps, isHovered } = useHover({ isDisabled });

  const dataAttributesProps = useDataAttributes(props);

  return (
    <div
      className={clsx(style.item, props.className, {
        [style.intent_selected]: isSelected,
        [style.intent_primary]: props.intent === MenuOptionItemIntent.Primary,
        [style.intent_danger]: props.intent === MenuOptionItemIntent.Danger,
        [style.item_disabled]: isDisabled,
        [style.item_focus]: isFocused || (props.useHover && isHovered),
      })}
      {...dataAttributesProps}
      {...(props.useHover ? hoverProps : {})}
      ref={triggerRef}
      id={props.id}
      style={props.style}
      data-testid="menu-option-item"
      data-e2e-intent={String(props.intent)}
      data-e2e-selected={String(isSelected)}
      data-e2e-disabled={String(isDisabled)}
    >
      <div className={style.things}>
        {props.icon || typeof props.icon === "string" ? (
          <div className={style.item_icon2} aria-hidden>
            <Icon icon={props.icon} style={{ fontSize: 18 }} />
          </div>
        ) : null}
        <div className={style.title_wrapper}>
          <div className={style.title} slot="label">
            {props.title}
            {props.tipId != undefined && <span id={props.tipId} />}
          </div>
          {props.description != undefined && (
            <div className={style.subtitle} slot="description">
              {props.description}
            </div>
          )}
        </div>
      </div>
      {(props.tag != undefined || !!isSelected || props.shortcut != undefined) && (
        <div className={style.things}>
          {(!isSelected || props.noCheck) &&
            (typeof props.tag === "string" || typeof props.tag === "number" ? (
              <Tag label={props.tag} intentColor={TagIntent.Outlined} className={style.tag} />
            ) : (
              props.tag
            ))}
          {!!isSelected && !props.noCheck && <SymbolIcon icon={"check"} className={style.selected_icon} />}
          {props.shortcut != undefined && (
            <kbd className={clsx(style.shortcut)}>
              <VisuallyHidden>(</VisuallyHidden>
              <Shortcut label={props.shortcut} />
              <VisuallyHidden>)</VisuallyHidden>
            </kbd>
          )}
        </div>
      )}
    </div>
  );
});

export const MainMenuOptionItem = forwardRef<HTMLDivElement, Omit<MenuOptionItemProps, "intent" | "shortcut">>(
  function MainMenuOptionItem(props, ref) {
    // Extract from CollectionItem
    const { isFocused, isDisabled } = props;
    const triggerRef = useDOMRef(ref);

    const dataAttributesProps = useDataAttributes(props);

    return (
      <div
        className={clsx(style.item, props.className, {
          // TODO: Improve isSelected with background!
          [style.item_disabled]: isDisabled,
          [style.item_focus]: isFocused,
        })}
        {...mergeProps(dataAttributesProps)}
        ref={triggerRef}
        id={props.id}
        style={props.style}
        data-testid="menu-option-item"
        data-e2e-disabled={String(isDisabled)}
      >
        <div className={style.things_main}>
          {props.icon || typeof props.icon === "string" ? (
            <div className={style.item_icon2} aria-hidden>
              <Icon icon={props.icon} style={{ fontSize: 18 }} />
            </div>
          ) : null}
          <div className={style.title_wrapper}>
            <div className={style.title}>{props.title}</div>
            {props.description != undefined && <div className={style.subtitle}>{props.description}</div>}
          </div>
        </div>
        {props.tag != undefined && (
          <div className={style.things}>
            {typeof props.tag === "string" || typeof props.tag === "number" ? (
              <Tag label={props.tag} intentColor={TagIntent.Outlined} className={style.tag} />
            ) : (
              props.tag
            )}
          </div>
        )}
      </div>
    );
  },
);
